import React, { useState } from 'react';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Box, Button, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { formatMinorToMajorAmount } from '../../../utils/currency';
import ProductPriceModal from './ProductPriceModal';
import { useGuardedNavigation } from '../../../permissions/useGuardedNavigation';

const emptyPriceRow = () => ({
  name: '',
  type: '',
  pricing_model: '',
  currency: 'usd',
  meter_code: '',
  unit_amount: '',
  is_active: true,
});

function rowToFormInitial(row) {
  if (!row) return {};
  const { id: _rhfId, ...rest } = row;
  return {
    ...rest,
    unit_amount: rest.unit_amount ?? '',
  };
}

export default function ProductPricesTable({ readOnly = false, isEdit = false }) {
  const theme = useTheme();
  const { control, watch } = useFormContext();
  const productType = watch('type');
  const { runGuardedCreate, runGuardedUpdate } = useGuardedNavigation('products');
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'prices',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalIndex, setModalIndex] = useState(null);

  const handleCloseModal = () => setModalOpen(false);

  const openAdd = () => {
    runGuardedCreate(() => {
      setModalMode('add');
      setModalIndex(null);
      setModalOpen(true);
    });
  };

  const openEdit = (index) => {
    runGuardedUpdate(() => {
      setModalMode('edit');
      setModalIndex(index);
      setModalOpen(true);
    });
  };

  const openView = (index) => {
    setModalMode('view');
    setModalIndex(index);
    setModalOpen(true);
  };

  const handleCopyId = async (value) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      // Ignore clipboard errors to avoid blocking table interactions.
    }
  };

  const handleModalSave = (data) => {
    const { stripe_price_id, ...rest } = data;
    const row = {
      ...rest,
      ...(stripe_price_id ? { stripe_price_id } : {}),
    };
    if (modalMode === 'add') {
      append(row);
    } else if (modalMode === 'edit' && modalIndex !== null) {
      update(modalIndex, { ...fields[modalIndex], ...row });
    }
  };

  const modalInitial =
    modalMode === 'add' ? emptyPriceRow() : modalIndex !== null ? rowToFormInitial(fields[modalIndex]) : {};

  const columns = [
    {
      field: 'stripe_price_id',
      headerName: 'Stripe Price ID',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.row.stripe_price_id;

        return (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%', height: '100%' }}
          >
            <Typography
              variant="body2"
              title={value || '—'}
              sx={{
                minWidth: 0,
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value || '—'}
            </Typography>
            <Tooltip title="Copy ID">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ ml: 0.5, flexShrink: 0 }}
                  aria-label={`copy-${value || 'id'}`}
                  disabled={!value}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleCopyId(value);
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 100 },
    { field: 'pricing_model', headerName: 'Pricing Model', flex: 0.9, minWidth: 100 },
    {
      field: 'unit_amount',
      headerName: 'Unit Amount',
      flex: 0.7,
      minWidth: 90,
      renderCell: (params) => {
        const v = params.row.unit_amount;
        return formatMinorToMajorAmount(v, { emptyValue: '—' });
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: readOnly ? 72 : 120,
      getActions: (params) => {
        const index = fields.findIndex((f) => f.id === params.id);
        if (index < 0) return [];
        if (readOnly) {
          return [
            <GridActionsCellItem
              key="view"
              icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
              label="View"
              onClick={() => openView(index)}
              sx={{ color: theme.palette.primary.main }}
            />,
          ];
        }
        return [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
            label="View"
            onClick={() => openView(index)}
            showInMenu
          />,
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon sx={{ fontSize: 18 }} />}
            label="Edit"
            onClick={() => openEdit(index)}
            showInMenu
          />,
        ];
      },
    },
  ];

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Prices</Typography>
        {!readOnly && !isEdit && (
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAdd}>
            Add price
          </Button>
        )}
      </Box>
      <Box sx={{ width: '100%', overflow: 'auto' }}>
        <DataGrid
          rows={fields}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          autoHeight
          hideFooter
          disableColumnMenu
          disableColumnSorting
          disableColumnFilter
          sx={{
            minHeight: fields.length ? 'auto' : 200,
          }}
        />
      </Box>
      <ProductPriceModal
        open={modalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
        initialData={modalInitial}
        onSave={handleModalSave}
        isEdit={isEdit}
        productType={productType}
      />
    </Box>
  );
}
